import React, { useState, useCallback, useEffect, useImperativeHandle } from 'react';
import { makeStyles } from 'react-scale/components/design-system';
import { TextInput } from 'design-system';
import { ChatBulkAuditResultToggle } from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditResultToggle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ChatBulkAuditBatch,
  SubtaskAssignment,
  SubtaskAssignmentAbbreviatedResponse,
} from '../SpeedAudit/types';
import { AuditResultV2 } from '@scale/shared/defaults';
import ChatBulkAuditTaskerInfoBlock from './ChatBulkAuditTaskerInfoBlock';
import { updateAttemptAudit, getAttemptAuditById } from './ChatBulkAuditApi';
import {
  getCorrespondingAttemptAuditForContributorFeedback,
  getAttemptAuditIdForContributorFeedback,
  getAuditResultPageUrl,
} from './utils';
import { Button, Icon } from '@scale/scaleui';
import { IUserJson } from '@scale/shared/types/user';
import { useSkipAuditAttemptsStore } from 'components/audits/ChatBulkAudit/hooks/useSkipAuditAttemptStore';
import { useAuditActions } from 'react-scale/components/ExpertManagement/stores/audit.store';
import ConfirmModal from './ConfirmModal'; // Ensure the path is correct
import { useAuditNavigationShortcuts } from './hooks/useAuditNavigationShortcuts';

interface ChatBulkAuditContributorFeedbackBarProps {
  // used for tracking pages on feedback
  auditBatch: ChatBulkAuditBatch;
  currentAttemptAudit: number;
  setCurrentAttemptAudit: (index: number) => void;
  // used for responses, cached layer
  subtaskAssignment?: SubtaskAssignment;
  subtaskAssignmentsLocalCopy: SubtaskAssignmentAbbreviatedResponse[];
  user: IUserJson;
  allowEdits: boolean;
  auditorHasClaim: boolean;
}

const useStyles = makeStyles(() => ({
  attemptSection: {
    borderBottom: '1px solid #E5E7EB',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    height: '100%',
  },
  containerSection: {
    padding: '8px 16px 12px 16px',
    marginTop: '0',
    width: '100%',
  },
  label: {
    fontWeight: 500,
    paddingBottom: '4px',
  },
  warningMessage: {
    padding: '8px 16px 12px 16px',
  },
  taskSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    paddingTop: '8px',
  },
  footer: {
    marginTop: 'auto',
    borderTop: '1px solid #E5E7EB',
    marginBottom: '35px',
  },
}));

function ChatBulkAuditContributorFeedbackBar(
  props: ChatBulkAuditContributorFeedbackBarProps,
  ref: $FIXME,
) {
  const {
    auditBatch,
    currentAttemptAudit,
    subtaskAssignment,
    subtaskAssignmentsLocalCopy,
    setCurrentAttemptAudit,
    auditorHasClaim,
    allowEdits,
  } = props;
  const ENABLE_EDIT_CONFIRM_MODAL = false;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAutosavingMessage, setShowAutosavingMessage] = useState<boolean>(false);
  const [grade, setGrade] = useState<AuditResultV2 | null>(null);
  const [publicTaskerFeedback, setPublicTaskerFeedback] = useState<string | undefined>(undefined);
  const [privateTaskerFeedback, setPrivateTaskerFeedback] = useState<string | undefined>(undefined);
  const [pageNumber, setPageNumber] = useState<string>((currentAttemptAudit + 1).toString());
  const [dirty, setDirty] = useState<boolean>(false);
  const [contributorId, setContributorId] = useState<string | undefined>(
    subtaskAssignment?.auditedAttempt?.attemptedBy,
  );
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [pendingAction, setPendingAction] = useState<() => void>(() => {});
  const setOpenChatBulkAuditResults = useAuditActions().setOpenChatBulkAuditResults;

  const skipAuditAttemptIds = useSkipAuditAttemptsStore(state => state.skipAuditAttemptIds);

  const classes = useStyles();
  const batchCompleted = auditBatch.completed;

  const setContributorGradeAndUpdateCachedCopy = useCallback(
    (newGrade: AuditResultV2 | null, skipSetDirty: boolean = false) => {
      setGrade(newGrade);
      if (!skipSetDirty) {
        setDirty(true);
      }
      if (!contributorId) {
        return;
      }
      const attemptAudit = getCorrespondingAttemptAuditForContributorFeedback(
        subtaskAssignmentsLocalCopy,
        contributorId,
      );
      if (!attemptAudit) {
        return;
      }
      if (attemptAudit.taskerScore) {
        attemptAudit.taskerScore.result = newGrade ?? AuditResultV2.Undecided;
      } else {
        attemptAudit.taskerScore = {
          attemptId: attemptAudit.auditLookup.id,
          errorCategories: [],
          result: newGrade ?? AuditResultV2.Undecided,
        };
      }
    },
    [contributorId],
  );
  const setPublicTaskerFeedbackAndUpdateCachedCopy = useCallback(
    (newPublicTaskerFeedback: string, skipSetDirty: boolean = false) => {
      setPublicTaskerFeedback(newPublicTaskerFeedback);
      if (!skipSetDirty) {
        setDirty(true);
      }
      if (!contributorId) {
        return;
      }
      const attemptAudit = getCorrespondingAttemptAuditForContributorFeedback(
        subtaskAssignmentsLocalCopy,
        contributorId,
      );
      if (!attemptAudit) {
        return;
      }
      attemptAudit.taskerFeedback = newPublicTaskerFeedback;
    },
    [contributorId],
  );
  const setPrivateTaskerFeedbackAndUpdateCachedCopy = useCallback(
    (newPrivateTaskerFeedback: string, skipSetDirty: boolean = false) => {
      setPrivateTaskerFeedback(newPrivateTaskerFeedback);
      if (!skipSetDirty) {
        setDirty(true);
      }
      if (!contributorId) {
        return;
      }
      const attemptAudit = getCorrespondingAttemptAuditForContributorFeedback(
        subtaskAssignmentsLocalCopy,
        contributorId,
      );
      if (!attemptAudit) {
        return;
      }
      if (attemptAudit.taskerScore) {
        attemptAudit.taskerScore.feedback = newPrivateTaskerFeedback;
      } else {
        attemptAudit.taskerScore = {
          attemptId: attemptAudit.auditLookup.id,
          errorCategories: [],
          result: grade ?? AuditResultV2.Undecided,
          feedback: newPrivateTaskerFeedback,
        };
      }
    },
    [contributorId],
  );

  const autosaveFeedback = useCallback(async () => {
    if (!dirty || auditBatch.completed) {
      return;
    }
    if (!contributorId) {
      return;
    }
    const attemptAudit = getCorrespondingAttemptAuditForContributorFeedback(
      subtaskAssignmentsLocalCopy,
      contributorId,
    );
    if (!attemptAudit) {
      setErrorMessage("Can't find contributor with id.");
    }
    const attemptAuditId = getAttemptAuditIdForContributorFeedback(
      auditBatch,
      subtaskAssignmentsLocalCopy,
      contributorId,
    );
    if (!attemptAuditId) {
      setErrorMessage("Can't find attempt audit id.");
      return;
    }

    try {
      setShowAutosavingMessage(true);
      await updateAttemptAudit({
        attemptAuditId,
        taskerId: contributorId,
        publicTaskerFeedback,
        privateTaskerFeedback,
        taskerScore: grade ?? undefined,
      });
      setDirty(false);
    } catch (err) {
      setErrorMessage('Autosave failed: ' + err);
    } finally {
      setShowAutosavingMessage(false);
    }
  }, [dirty, grade, publicTaskerFeedback, privateTaskerFeedback, contributorId]);
  useImperativeHandle(ref, () => ({
    autosaveFeedback,
  }));

  useEffect(() => {
    if (subtaskAssignment?.auditedAttempt?.attemptedBy) {
      setContributorId(subtaskAssignment?.auditedAttempt?.attemptedBy);
    }
  }, [subtaskAssignment?.auditedAttempt?.attemptedBy, contributorId]);

  useEffect(() => {
    if (!contributorId) {
      return;
    }
    const attemptAudit = getCorrespondingAttemptAuditForContributorFeedback(
      subtaskAssignmentsLocalCopy,
      contributorId,
    );
    if (!attemptAudit) {
      return;
    }
    const attemptAuditId = getAttemptAuditIdForContributorFeedback(
      auditBatch,
      subtaskAssignmentsLocalCopy,
      contributorId,
    );
    if (!attemptAuditId) {
      return;
    }
    getAttemptAuditById(attemptAuditId)
      .then(newAttemptAudit => {
        setErrorMessage(null);
        setContributorGradeAndUpdateCachedCopy(
          (newAttemptAudit.taskerScore?.result as AuditResultV2) ?? null,
          true,
        );
        if (newAttemptAudit.taskerFeedback) {
          setPublicTaskerFeedbackAndUpdateCachedCopy(newAttemptAudit.taskerFeedback, true);
        }
        if (newAttemptAudit.taskerScore?.feedback) {
          setPrivateTaskerFeedbackAndUpdateCachedCopy(newAttemptAudit.taskerScore?.feedback, true);
        }
        return;
      })
      .catch(err => {
        setErrorMessage(JSON.stringify(err.response.data));
      })
      .finally(() => {
        setPageNumber((currentAttemptAudit + 1).toString());
      })
      .catch(() => {
        // used to satisfy the linter
      });
  }, [
    contributorId,
    setContributorGradeAndUpdateCachedCopy,
    setPublicTaskerFeedbackAndUpdateCachedCopy,
    setPrivateTaskerFeedbackAndUpdateCachedCopy,
  ]);

  useEffect(() => {
    const handleNavigationAway = (event: BeforeUnloadEvent) => {
      if (dirty && !auditBatch.completed) {
        // eslint-disable promise/always-return Ignore for beforeunload event handling
        autosaveFeedback();
        event.preventDefault();
        event.returnValue = ''; // Show warning message before navigating away
      }
    };
    window.addEventListener('beforeunload', handleNavigationAway);

    return () => {
      window.removeEventListener('beforeunload', handleNavigationAway);
    };
  }, [dirty, autosaveFeedback]);

  const navigateToPage = (nextAttemptAudit: number) => {
    if (ENABLE_EDIT_CONFIRM_MODAL && (auditorHasClaim || allowEdits)) {
      setPendingAction(() => () => proceedToPage(nextAttemptAudit));
      setShowConfirmModal(true);
      return;
    }
    proceedToPage(nextAttemptAudit);
  };

  useAuditNavigationShortcuts(currentAttemptAudit, auditBatch.attemptAudits.length, navigateToPage);

  const proceedToPage = (nextAttemptAudit: number) => {
    if (nextAttemptAudit >= 0 && nextAttemptAudit < auditBatch.attemptAudits.length) {
      autosaveFeedback()
        .finally(() => {
          setCurrentAttemptAudit(nextAttemptAudit);
          setPageNumber((nextAttemptAudit + 1).toString());
          const newContributorId = subtaskAssignmentsLocalCopy[nextAttemptAudit].attemptTaskerId;
          if (newContributorId) {
            if (newContributorId !== contributorId) {
              setContributorId(newContributorId);
              const attemptAudit = getCorrespondingAttemptAuditForContributorFeedback(
                subtaskAssignmentsLocalCopy,
                newContributorId,
              );
              if (attemptAudit) {
                // update with new task feedback so far
                setGrade((attemptAudit.taskerScore?.result as AuditResultV2) ?? null);
                setPrivateTaskerFeedback(attemptAudit.taskerScore?.feedback);
                setPublicTaskerFeedback(attemptAudit.taskerFeedback);
                return;
              }
            } else {
              // nothing to update, same contributor
              return;
            }
          }
          // update to null if we can't find contributor or attempt id
          setGrade(null);
          setContributorId(undefined);
          setPrivateTaskerFeedback(undefined);
          setPublicTaskerFeedback(undefined);
        })
        .catch(err => {
          // This should never actually happen, but adding the catch here to satisfy lint.
          throw err;
        });
    }
  };

  const handleSubmitAudit = () => {
    if (ENABLE_EDIT_CONFIRM_MODAL && (auditorHasClaim || allowEdits)) {
      setPendingAction(() => () => submitAudit());
      setShowConfirmModal(true);
      return;
    }
    submitAudit();
  };

  const submitAudit = () => {
    if (!window.location.pathname.includes('corp')) {
      autosaveFeedback()
        .then(() => {
          setOpenChatBulkAuditResults(true);
          return;
        })
        .catch(() => {
          setErrorMessage("Failed to save feedback. Can't navigate away");
        });
    }
    const resultPageUrl = getAuditResultPageUrl(auditBatch._id);
    if (resultPageUrl) {
      window.location.href = resultPageUrl;
    }
  };

  const disableFeedback =
    batchCompleted || !contributorId || skipAuditAttemptIds.has(subtaskAssignment?._id ?? '');

  return (
    <div
      className={classes.container + ' bg-neutral-0 py-3 items-center border-b border-neutral-200'}
    >
      <div className={classes.containerSection + ' ' + classes.attemptSection}>
        {props.subtaskAssignment?.auditedAttempt &&
          props.subtaskAssignment?.auditedAttempt?.attemptedBy && (
            <ChatBulkAuditTaskerInfoBlock
              auditBatch={auditBatch}
              attemptAudit={props.subtaskAssignment}
              subtaskAssignmentsLocalCopy={props.subtaskAssignmentsLocalCopy}
              contributorId={props.subtaskAssignment?.auditedAttempt?.attemptedBy}
              attempt={props.subtaskAssignment?.auditedAttempt}
            />
          )}
      </div>
      <div className={classes.containerSection}>
        <p className={classes.label}>How reliable is this contributor?</p>
        <ChatBulkAuditResultToggle
          value={grade}
          onChange={setContributorGradeAndUpdateCachedCopy}
          disabled={disableFeedback}
        />
      </div>
      <div className={classes.containerSection}>
        <div className="flex">
          <p className={classes.label}>Overall Contributor Feedback (External)</p>
          <Icon
            icon="info-circle"
            size="sm"
            tooltip="Persisted in attemptaudits.taskerFeedback and sent to the contributor as a mandatory course"
            className="ml-2"
          />
        </div>
        <TextInput
          placeholder="Add additional comments about your rating for the contributor"
          multiline
          value={publicTaskerFeedback ?? ''}
          style={{ width: '100%' }}
          disabled={disableFeedback}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPublicTaskerFeedbackAndUpdateCachedCopy((e.target as HTMLInputElement).value)
          }
        />
      </div>
      <div className={classes.containerSection}>
        <div className="flex">
          <p className={classes.label}>Additional Comments (internal)</p>
          <Icon
            icon="info-circle"
            size="sm"
            tooltip="Persisted in attemptaudits.taskerScore.feedback"
            className="ml-2"
          />
        </div>
        <TextInput
          placeholder="Add additional comments for internal reviewers"
          multiline
          value={privateTaskerFeedback ?? ''}
          style={{ width: '100%' }}
          disabled={disableFeedback}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPrivateTaskerFeedbackAndUpdateCachedCopy((e.target as HTMLInputElement).value)
          }
        />
      </div>
      {errorMessage && <p className={classes.warningMessage}>{errorMessage}</p>}
      {showAutosavingMessage && <p className={classes.warningMessage}>Autosaving…</p>}
      <div className={classes.footer + ' flex justify-between items-center w-full'}>
        <div className={'flex items-center'}>
          <button
            className={`bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded ${
              currentAttemptAudit === 0 ? 'opacity-50 cursor-not-allowed' : 'disabled-button'
            }`}
            onClick={() => {
              navigateToPage(currentAttemptAudit - 1);
            }}
            disabled={currentAttemptAudit === 0}
          >
            <FontAwesomeIcon icon="chevron-left" />
          </button>

          <div className={classes.taskSection}>
            Task
            <TextInput
              value={pageNumber}
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === 'Enter') {
                  const newPageNumber = parseInt((event.target as HTMLInputElement).value);
                  if (newPageNumber - 1 === currentAttemptAudit) {
                    return;
                  }

                  if (!isNaN(newPageNumber)) {
                    navigateToPage(newPageNumber - 1);
                  } else {
                    setErrorMessage('Please enter a valid page number');
                  }
                }
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setPageNumber(event.target.value);
              }}
              style={{
                width: '6ch',
                marginLeft: '4px',
                marginRight: '4px',
                borderRadius: '4px',
              }}
            />
            of {auditBatch.attemptAudits.length}
          </div>
          <button
            className={`bg-white hover:bg-gray-100 text-gray-700 font-bold py-2 px-4 rounded ${
              currentAttemptAudit === auditBatch.attemptAudits.length - 1
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            onClick={() => {
              navigateToPage(currentAttemptAudit + 1);
            }}
            disabled={currentAttemptAudit === auditBatch.attemptAudits.length - 1}
          >
            <FontAwesomeIcon icon="chevron-right" />
          </button>
        </div>
        <div>
          <Button className="mt-2 mr-6" size="sm" variant="primary" onClick={handleSubmitAudit}>
            {auditBatch.completed ? 'View Audit Summary' : 'Review and Complete'}
          </Button>
        </div>
      </div>
      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() => {
            setShowConfirmModal(false);
            pendingAction();
          }}
          message="If you have made edits to the task that you wish to keep, please remember to save them before navigating away. Are you sure you want to navigate away?"
        />
      )}
    </div>
  );
}

export default React.memo(React.forwardRef(ChatBulkAuditContributorFeedbackBar));
